import { forwardRef, useCallback, useMemo, useRef } from 'react';
import { pt } from '../layout';
import { resolveWireStyle, resolveRingStyle } from '../style';
import { VIEWBOX } from '../constants';
import { useEditDrag } from '../useEditDrag';
import Wire from './Wire';
import Ring from './Ring';
import ArrowMarkerDefs from './ArrowMarkerDefs';
import SunDisc from './SunDisc';
import GroupNode from './GroupNode';
import ProgramNode from './ProgramNode';
import PersonNode from './PersonNode';
import DragPreview from './DragPreview';
import ResizeHandles from './ResizeHandles';
import type { OrgChartDoc, Selection } from '../types';
import type { GroupEntry } from '../layout';

interface OrgChartCanvasProps {
  doc: OrgChartDoc;
  layout: GroupEntry[];
  selection: Selection;
  editMode: boolean;
  onSelect: (s: Selection) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dispatch?: (action: any) => void;
  onActivate?: (s: Selection) => void;
}

const OrgChartCanvas = forwardRef<SVGSVGElement, OrgChartCanvasProps>(
  function OrgChartCanvas({ doc, layout, selection, editMode, onSelect, dispatch = () => {}, onActivate = () => {} }, ref) {
  const settings = doc.settings;
  const sunR = settings.sunR;
  const isPicked = selection !== null;

  const svgElRef = useRef<SVGSVGElement | null>(null);
  const setSvgRef = useCallback((node: SVGSVGElement | null) => {
    svgElRef.current = node;
    if (typeof ref === 'function') ref(node);
    else if (ref) (ref as React.MutableRefObject<SVGSVGElement | null>).current = node;
  }, [ref]);

  const { onPointerDown, onPointerMove, onPointerUp, onPointerCancel, dragPreviewRef, liveDragPosition, liveResize } =
    useEditDrag({ dispatch, layout, editMode, svgRef: svgElRef, sunX: doc.sun.x, sunY: doc.sun.y, onActivate });

  const sunPos = liveDragPosition?.kind === 'sun'
    ? { x: liveDragPosition.x, y: liveDragPosition.y }
    : { x: doc.sun.x, y: doc.sun.y };

  const resolveGroupCenter = (ge: GroupEntry) => {
    if (liveDragPosition?.kind === 'group' && liveDragPosition.gi === ge.gi) {
      return { x: liveDragPosition.x, y: liveDragPosition.y };
    }
    return { x: ge.gcx, y: ge.gcy };
  };

  const resolveProgramCenter = (pe: GroupEntry['progs'][0], ge: GroupEntry) => {
    if (liveDragPosition?.kind === 'prog' && liveDragPosition.gi === pe.gi && liveDragPosition.pi === pe.pi) {
      return { x: liveDragPosition.x, y: liveDragPosition.y };
    }
    if (pe.solo) return resolveGroupCenter(ge);
    return { x: pe.pcx, y: pe.pcy };
  };

  const resolveSunR = () => (liveResize?.target === 'sun' ? liveResize.value : sunR);
  const resolveGroupDiscR = (ge: GroupEntry) => (
    liveResize?.target === 'gdisc' && liveResize.gi === ge.gi ? liveResize.value : ge.discR
  );
  const resolveGroupRingR = (ge: GroupEntry) => (
    liveResize?.target === 'gring' && liveResize.gi === ge.gi ? liveResize.value : ge.progs[0].ring
  );
  const resolveProgDiscR = (pe: GroupEntry['progs'][0]) => (
    liveResize?.target === 'pdisc' && liveResize.gi === pe.gi && liveResize.pi === pe.pi ? liveResize.value : pe.discR
  );
  const resolveProgRingR = (pe: GroupEntry['progs'][0]) => (
    liveResize?.target === 'pring' && liveResize.gi === pe.gi && liveResize.pi === pe.pi ? liveResize.value : pe.ring
  );

  // Stabilized so GroupNode/ProgramNode/PersonNode can all receive the SAME
  // function reference across the rAF-driven re-renders that happen during a
  // drag/resize (only liveDragPosition/liveResize change per frame; editMode
  // and the `onSelect` prop do not). A fresh arrow function per node per render
  // would defeat React.memo on those children, forcing every node to re-render
  // every frame even when its own position/style didn't change.
  const handleReadModeSelect = useCallback((sel: Selection) => {
    if (!editMode) onSelect(sel);
  }, [editMode, onSelect]);

  // Wire styles depend only on `layout` (group/program style overrides) and
  // `settings`, both of which stay referentially stable for the duration of a
  // drag/resize (they're only replaced via dispatch on pointer-up, see
  // useOrgChart.ts / useEditDrag.ts). Precomputing them here means Wire
  // receives the SAME `resolved` object reference on every rAF re-render
  // where nothing actually changed, which is what lets React.memo on Wire
  // skip re-rendering the ~unaffected wires during a drag frame.
  const groupWireStyles = useMemo(() => {
    const map = new Map<number, ReturnType<typeof resolveWireStyle>>();
    layout.forEach(ge => {
      map.set(ge.gi, resolveWireStyle(
        { color: ge.gr.wireColor, width: ge.gr.wireWidth, dash: ge.gr.wireDash, arrow: ge.gr.wireArrow },
        settings,
      ));
    });
    return map;
  }, [layout, settings]);

  const progWireStyles = useMemo(() => {
    const map = new Map<string, ReturnType<typeof resolveWireStyle>>();
    layout.forEach(ge => {
      ge.progs.forEach(pe => {
        map.set(`${pe.gi}-${pe.pi}`, resolveWireStyle(
          { color: pe.prog.wireColor, width: pe.prog.wireWidth, dash: pe.prog.wireDash, arrow: pe.prog.wireArrow },
          settings,
        ));
      });
    });
    return map;
  }, [layout, settings]);

  const isSunSelected = selection?.kind === 'sun';

  const isGroupDiscSelected = (gi: number, solo: boolean) => {
    if (!selection) return false;
    if (selection.kind === 'group' && selection.gi === gi) return true;
    if (solo && selection.kind === 'prog' && selection.gi === gi) return true;
    return false;
  };
  const isProgSelected = (gi: number, pi: number) => {
    if (!selection) return false;
    if (selection.kind === 'prog' && selection.gi === gi && selection.pi === pi) return true;
    if (selection.kind === 'group' && selection.gi === gi) return true;
    return false;
  };
  const isPersonSelected = (gi: number, pi: number, ki: number) => {
    if (!selection) return false;
    if (selection.kind === 'person' && selection.gi === gi && selection.pi === pi && selection.ki === ki) return true;
    if (selection.kind === 'prog' && selection.gi === gi && selection.pi === pi) return true;
    if (selection.kind === 'group' && selection.gi === gi) return true;
    return false;
  };

  const wireColors: string[] = [];
  layout.forEach(ge => {
    if (settings.showSpokes && ge.gr.wireShow !== false) {
      wireColors.push(ge.gr.wireColor || settings.wireColor);
    }
    ge.progs.forEach(pe => {
      if (!pe.solo && settings.showSpokes && pe.prog.wireShow !== false) {
        wireColors.push(pe.prog.wireColor || settings.wireColor);
      }
    });
  });

  return (
    <svg ref={setSvgRef} className={`dial${isPicked ? ' picked' : ''}${editMode ? ' editing' : ''}`} viewBox={`${VIEWBOX.minX} ${VIEWBOX.minY} ${VIEWBOX.width} ${VIEWBOX.height}`}
      role="group" aria-label="ITPMS orbit org chart"
      onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerCancel}
    >
      <defs><ArrowMarkerDefs colors={wireColors} /></defs>

      <g className="wires">
        {layout.map(ge => {
          if (!settings.showSpokes || ge.gr.wireShow === false) return null;
          const gCenter = resolveGroupCenter(ge);
          const a = pt(sunR + 2, ge.away, sunPos.x, sunPos.y);
          const b = pt(ge.discR + 3, ge.away + 180, gCenter.x, gCenter.y);
          const resolved = groupWireStyles.get(ge.gi)!;
          return (
            <g key={`w-${ge.gi}`}>
              <Wire className="wire" x1={a.x} y1={a.y} x2={b.x} y2={b.y} resolved={resolved} />
              <circle className="joint" cx={gCenter.x} cy={gCenter.y} r={3} style={{ fill: ge.gr.wireColor || settings.wireColor }} />
            </g>
          );
        })}
        {layout.flatMap(ge => ge.progs.map(pe => {
          if (pe.solo || !settings.showSpokes || pe.prog.wireShow === false) return null;
          const gCenter = resolveGroupCenter(ge);
          const pCenter = resolveProgramCenter(pe, ge);
          const gEdge = pt(ge.discR + 2, pe.pang, gCenter.x, gCenter.y);
          const pEdge = pt(pe.discR + 3, pe.pang + 180, pCenter.x, pCenter.y);
          const resolved = progWireStyles.get(`${pe.gi}-${pe.pi}`)!;
          return (
            <Wire key={`sp-${pe.gi}-${pe.pi}`} className="spoke" x1={gEdge.x} y1={gEdge.y} x2={pEdge.x} y2={pEdge.y} resolved={resolved} />
          );
        }))}
      </g>

      <g className="orbits">
        <circle className="halo" cx={sunPos.x} cy={sunPos.y} r={sunR + 30} strokeDasharray="2 12" opacity={0.4} />
        {layout.map(ge => {
          if (ge.nProgs !== 1 || !settings.showRings || ge.gr.ringShow === false) return null;
          const gCenter = resolveGroupCenter(ge);
          const resolved = resolveRingStyle(ge.gr, settings, 'var(--teal)');
          return <Ring key={`o1-${ge.gi}`} className="orbit1" cx={gCenter.x} cy={gCenter.y} r={resolveGroupRingR(ge)} resolved={resolved} />;
        })}
        {layout.flatMap(ge => ge.progs.map(pe => {
          if (pe.solo || !settings.showRings || pe.prog.ringShow === false || pe.people.length === 0) return null;
          const pCenter = resolveProgramCenter(pe, ge);
          const resolved = resolveRingStyle(pe.prog, settings, pe.prog.color || ge.gr.color || 'var(--slate)');
          return <Ring key={`o2-${pe.gi}-${pe.pi}`} className="orbit2" cx={pCenter.x} cy={pCenter.y} r={resolveProgRingR(pe)} resolved={resolved} />;
        }))}
      </g>

      <g className="discs">
        <SunDisc cx={sunPos.x} cy={sunPos.y} r={resolveSunR()} org={doc.org}
          isSelected={!!isSunSelected} onSelect={() => handleReadModeSelect({ kind: 'sun' })} />
        {layout.map(ge => {
          const gCenter = resolveGroupCenter(ge);
          return (
            <GroupNode key={`g-${ge.gi}`} gcx={gCenter.x} gcy={gCenter.y} discR={resolveGroupDiscR(ge)} gr={ge.gr} gi={ge.gi}
              nProgs={ge.nProgs} solo={ge.solo}
              isSelected={isGroupDiscSelected(ge.gi, ge.solo)}
              onSelect={handleReadModeSelect}
              settings={settings} />
          );
        })}
        {layout.flatMap(ge => ge.progs.map(pe => {
          if (pe.solo) return null;
          const pCenter = resolveProgramCenter(pe, ge);
          return (
            <ProgramNode key={`p-${pe.gi}-${pe.pi}`} pcx={pCenter.x} pcy={pCenter.y} discR={resolveProgDiscR(pe)} ring={pe.ring}
              prog={pe.prog} groupColor={ge.gr.color || null} gi={pe.gi} pi={pe.pi} nP={pe.people.length}
              isSelected={isProgSelected(pe.gi, pe.pi)}
              onSelect={() => handleReadModeSelect({ kind: 'prog', gi: pe.gi, pi: pe.pi })}
              settings={settings} />
          );
        }))}
      </g>

      <g className="boxes" />

      <g className="people">
        {layout.flatMap(ge => ge.progs.flatMap(pe => {
          const pCenter = resolveProgramCenter(pe, ge);
          const dx = pCenter.x - pe.pcx, dy = pCenter.y - pe.pcy;
          return pe.people.map(entry => (
            <PersonNode key={`pp-${pe.gi}-${pe.pi}-${entry.ki}`} px={entry.x + dx} py={entry.y + dy} refX={pCenter.x} refY={pCenter.y}
              person={entry.p} gi={pe.gi} pi={pe.pi} ki={entry.ki}
              isSelected={isPersonSelected(pe.gi, pe.pi, entry.ki)}
              onSelect={handleReadModeSelect}
              settings={settings} />
          ));
        }))}
      </g>

      <g className="dragfx">
        <DragPreview ref={dragPreviewRef} />
      </g>
      <g className="handles">
        {editMode && <ResizeHandles selection={selection} layout={layout} sunX={sunPos.x} sunY={sunPos.y} sunR={settings.sunR} />}
      </g>
    </svg>
  );
});

export default OrgChartCanvas;
